/* eslint-disable @typescript-eslint/no-explicit-any */

import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import * as jwt_decode from 'jwt-decode';
import { Observable, throwError, of, interval } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { RoutingService } from './routing.service';
import { ConfigService } from './config.service';
import { NgHttpCachingService } from 'ng-http-caching';
import { BannerService } from './ai-banner.service';
import {
  getScoreNameInfo,
  getScoresInfo,
  getScoreType,
} from '../shared/utils/score-type-details';
import {
  UserStateService,
  DEFAULT_FEATURE_FLAGS,
} from '../shared/services/user-state.service';
import { CustomerApiService } from '../shared/services/customer-api/customer-api.service';
import { UserActivityApiService } from '../shared/services/customer-api/user-activity-api.service';
import { PortfolioApiService } from '../shared/services/customer-api/portfolio-api.service';
import { FeatureFlagsService } from '../shared/services/customer-api/feature-flags.service';
import { QueryApiService } from '../shared/services/customer-api/query-api.service';
import { TokenService } from '../shared/services/customer-api/token.service';
import { UserInfoService } from '../shared/services/user-info.service';

/**
 * This service manage all the user activity like login, report, annotation, portfolio, query etc...
 */
@Injectable()
export class AuthService {
  get token(): string {
    return this.tokenService.token;
  }
  set token(value: string) {
    this.tokenService.token = value;
  }

  get tokenDataAccess(): string {
    return this.tokenService.tokenDataAccess;
  }
  set tokenDataAccess(value: string) {
    this.tokenService.tokenDataAccess = value;
  }

  get jwtAuthorized(): boolean {
    return this.tokenService.jwtAuthorized;
  }
  set jwtAuthorized(value: boolean) {
    this.tokenService.jwtAuthorized = value;
  }

  /**
   * this is a duplicate of the token
   */
  userdata: any;

  /**
   * all information about the user
   */
  get accountInfo$() {
    return this.featureFlagsService.accountInfo$;
  }

  /**
   * cockpit settings (feature flags included if present in the backend)
   */
  get cockpitSettings() {
    return this.featureFlagsService.cockpitSettings;
  }
  set cockpitSettings(v) {
    this.featureFlagsService.cockpitSettings = v;
  }

  /**
   * cockpit settings for scorelab's scores
   */
  get scorelabSettings() {
    return this.userState.scorelabSettings;
  }
  set scorelabSettings(v) {
    this.userState.scorelabSettings = v;
  }

  /**
   * show in newsfeed
   */
  get showArticleSentiment() {
    return this.featureFlagsService.showArticleSentiment;
  }
  set showArticleSentiment(v) {
    this.featureFlagsService.showArticleSentiment = v;
  }

  /**
   * user related feature flags
   */
  get featureFlags() {
    return this.userState.featureFlags;
  }
  set featureFlags(v) {
    this.userState.featureFlags = v;
  }

  /**
   * entities send as an array in request's body
   */
  get payloadEntities() {
    return this.userState.payloadEntities;
  }
  set payloadEntities(v) {
    this.userState.payloadEntities = v;
  }

  /**
   * query's data
   */
  get querys() {
    return this.userState.querys;
  }
  set querys(v) {
    this.userState.querys = v;
  }

  /**
   * active query
   */
  get iquery() {
    return this.userState.iquery;
  }
  set iquery(v) {
    this.userState.iquery = v;
  }

  /**
   * query selected also a duplicate
   */
  get query() {
    return this.userState.query;
  }
  set query(v) {
    this.userState.query = v;
  }

  /**
   * selected query to be used in the newsfeed
   */
  get queryForNewsfeed() {
    return this.userState.queryForNewsfeed;
  }
  set queryForNewsfeed(v) {
    this.userState.queryForNewsfeed = v;
  }

  /**
   * Warning/caution flag indicators. Single source of truth lives on
   * UserStateService — the boolean setter emits through the subject so
   * readers and subscribers stay in sync (no manual pairing required).
   */
  get showWarningFlag(): boolean {
    return this.userState.showWarningFlag;
  }
  set showWarningFlag(v: boolean) {
    this.userState.showWarningFlag = v;
  }
  get showWarningFlag$() {
    return this.userState.showWarningFlag$;
  }
  get showCautionFlag(): boolean {
    return this.userState.showCautionFlag;
  }
  set showCautionFlag(v: boolean) {
    this.userState.showCautionFlag = v;
  }
  get showCautionFlag$() {
    return this.userState.showCautionFlag$;
  }

  /**
   * subscribe every 15 minutes to check if the token is expired, if expired back to login,
   * validate it to get new token and save it in the session storage
   */
  constructor(
    private router: Router,
    public routing: RoutingService,
    public config: ConfigService,
    private ngHttpCachingService: NgHttpCachingService,
    private bannerService: BannerService,
    private userState: UserStateService,
    private customerApi: CustomerApiService,
    private userActivityApi: UserActivityApiService,
    private portfolioApi: PortfolioApiService,
    private queryApi: QueryApiService,
    private featureFlagsService: FeatureFlagsService,
    private tokenService: TokenService,
    private userInfo: UserInfoService,
  ) {
    this.tokenService.configure({
      useStorage: this.config.appConfig.setup.useStorage,
      isReaderContext: this.routing.isReader() || this.routing.isStoryReader(),
    });

    interval(900000).subscribe(() => {
      if (!this.routing.isReader() && !this.routing.isStoryReader()) {
        const user = this.authToken();
        if (user) {
          const expire = new Date(user.exp * 1000);
          const now = new Date();
          if (expire < now) {
            this.router.navigate(['/login']);
          }
        } else {
          if (!this.routing.isLogin()) {
            this.router.navigate(['/login']);
          }
        }
        if (this.token) {
          this.customerApi
            .post<any>('/v5/validate/jwt', { token: this.token })
            .subscribe({
              next: (result) => {
                this.token = result.token;
                if (this.config.appConfig.setup.useStorage) {
                  localStorage.setItem('token', this.token);
                }
                this.tokenDataAccess = result.data_access_token;
                if (this.config.appConfig.setup.useStorage) {
                  localStorage.setItem('tokenDataAccess', this.tokenDataAccess);
                }
              },
              error: () => {
                this.router.navigate(['/login']);
              },
            });
        }
      }
    });
    window.addEventListener('storage', (event) => {
      const currentUser = event.newValue
        ? jwt_decode(event.newValue).email
        : null;
      const previousUser = event.oldValue
        ? jwt_decode(event.oldValue).email
        : null;
      if (
        event.key === 'token' &&
        (!currentUser ||
          (currentUser && previousUser && currentUser !== previousUser))
      ) {
        this.router.navigate(['/login']);
      }
    });
  }

  // ERROR ---------------------------------------------------------------------

  /**
   * Console log error in case of fail request
   */
  private handleError(error: HttpErrorResponse) {
    let message = 'Something bad happened please try again later.';
    if (error.error instanceof ErrorEvent) {
      console.error('An error occurred:', error.error.message);
      message = error.error.message;
    } else {
      console.error(
        `Backend returned code ${error.status}, ` + `body was: ${error.error}`,
      );
      message = error.error;
      // 401 handling is centralized in AuthInterceptor
    }
    return throwError(() => new Error(message));
  }

  // FEATURE FLAGS ------------------------------------------------------------

  /**
   * Loads remote user-specific configuration after login. Delegates to
   * FeatureFlagsService.load — see that service for the orchestration.
   */
  loadFeatureFlags(token: string): Observable<void> {
    return this.featureFlagsService.load(token);
  }

  // TRACKING USER INTERACTIONS WITH COCKPIT

  /**
   * track users login sessions
   */
  trackLoginSession(isImpersonated): Observable<any> {
    const params = isImpersonated ? { impersonated: 'true' } : undefined;
    return this.customerApi
      .get('/v1/cockpit_sessions/start', params)
      .pipe(catchError((error) => this.handleError(error)));
  }

  updateAccountInfo(accountInfo: any) {
    this.featureFlagsService.updateAccountInfo(accountInfo);
  }

  /**
   * Score-info delegates. Implementation lives in score-type-details.ts;
   * AuthService just forwards the user's current scorelabSettings. Callers
   * may import the utility functions directly — these wrappers exist for
   * template binding ergonomics (no constructor injection needed in HTML).
   */
  getScoreName() {
    return getScoreNameInfo(this.scorelabSettings);
  }

  getScoresInfo(id?: string) {
    return getScoresInfo(this.scorelabSettings, id);
  }

  getScoreType(id?: string) {
    return getScoreType(this.scorelabSettings, id);
  }

  // AUTH ---------------------------------------------------------------------

  /**
   * autentification of the user login
   */
  authLogin(email, password): Observable<any> {
    return this.customerApi
      .post<any>('/v5/authenticate', { email, password })
      .pipe(
        tap((result) => {
          if (!result['two_factor_auth']) {
            const data: any = result;
            this.token = data.token;
            const user = jwt_decode(data.token);
            if (this.config.appConfig.setup.useStorage) {
              localStorage.setItem('token', this.token);
            }
            this.tokenDataAccess = data.data_access_token;
            if (this.config.appConfig.setup.useStorage) {
              localStorage.setItem('tokenDataAccess', this.tokenDataAccess);
            }
            this.loadFeatureFlags(this.token).subscribe();
            this.userActivityApi.getSeenArticles().subscribe((articles) => {
              this.userActivityApi.seenArticles = articles;
            });
            if (this.config.appConfig.setup.useStorage) {
              if (sessionStorage.getItem('track_login') !== user.email) {
                sessionStorage.setItem('track_login', user.email);
                if (localStorage.getItem('impersonate') === user.email) {
                  this.trackLoginSession(true).subscribe();
                } else {
                  this.trackLoginSession(false).subscribe();
                }
              }
            }

            // Check and show AI banner modal after successful login
            setTimeout(() => {
              this.bannerService.checkAndShowBanner();
            }, 1000); // Small delay to ensure the user has navigated
          }
        }),
        catchError((error) => this.handleError(error)),
      );
  }

  /**
   * autentification of the user login
   */
  authLogin2fa(email, code): Observable<any> {
    return this.customerApi
      .post<any>('/v5/authenticate2f', { email, code })
      .pipe(
        tap((result) => {
          const data: any = result;
          this.token = data.token;
          const user = jwt_decode(data.token);
          if (this.config.appConfig.setup.useStorage) {
            localStorage.setItem('token', this.token);
          }
          this.tokenDataAccess = data.data_access_token;
          if (this.config.appConfig.setup.useStorage) {
            localStorage.setItem('tokenDataAccess', this.tokenDataAccess);
          }
          this.loadFeatureFlags(this.token).subscribe();
          this.userActivityApi.getSeenArticles().subscribe((articles) => {
            this.userActivityApi.seenArticles = articles;
          });
          if (this.config.appConfig.setup.useStorage) {
            if (sessionStorage.getItem('track_login') !== user.email) {
              sessionStorage.setItem('track_login', user.email);
              if (localStorage.getItem('impersonate') === user.email) {
                this.trackLoginSession(true).subscribe();
              } else {
                this.trackLoginSession(false).subscribe();
              }
            }
          }

          // Check and show AI banner modal after successful 2FA login
          setTimeout(() => {
            this.bannerService.checkAndShowBanner();
          }, 1000); // Small delay to ensure the user has navigated
        }),
        catchError((error) => this.handleError(error)),
      );
  }

  /**
   * autentification of the token
   */
  authLoged(): Observable<any> {
    if (this.config.appConfig.setup.useStorage) {
      this.token = localStorage.getItem('token');
    }
    if (!this.token) {
      return of({});
    }
    return this.customerApi
      .post<any>('/v5/validate/jwt', { token: this.token })
      .pipe(
        tap((result) => {
          const data: any = result;
          this.token = data.token;
          const user = jwt_decode(data.token);
          this.tokenDataAccess = data.data_access_token;
          if (!this.routing.isReader() && !this.routing.isStoryReader()) {
            if (this.config.appConfig.setup.useStorage) {
              localStorage.setItem('token', this.token);
            }
            if (this.config.appConfig.setup.useStorage) {
              localStorage.setItem('tokenDataAccess', this.tokenDataAccess);
            }
            this.loadFeatureFlags(this.token).subscribe();
            this.userActivityApi.getSeenArticles().subscribe((articles) => {
              this.userActivityApi.seenArticles = articles;
            });
          }
          this.jwtAuthorized = true;
          if (this.config.appConfig.setup.useStorage) {
            if (sessionStorage.getItem('track_login') !== user.email) {
              sessionStorage.setItem('track_login', user.email);
              if (localStorage.getItem('impersonate') === user.email) {
                this.trackLoginSession(true).subscribe();
              } else {
                this.trackLoginSession(false).subscribe();
              }
            }
          }
        }),
        catchError((error) => this.handleError(error)),
      );
  }

  /**
   * autentification of the token
   */
  jwtAuth(token): Observable<any> {
    return this.customerApi.post<any>('/v5/validate/jwt', { token }).pipe(
      tap((result) => {
        const data: any = result;
        this.token = data.token;
        const user = jwt_decode(data.token);
        this.userdata = user;
        this.tokenDataAccess = data.data_access_token;
        let readonlyToken = false;
        if (
          user &&
          ((user.roles && user.roles.includes('WIDGET_READONLY')) ||
            (user.roles && user.roles.includes('WIDGET_READONLY')))
        ) {
          readonlyToken = true;
        }
        if (
          !this.routing.isReader() &&
          !this.routing.isStoryReader() &&
          !readonlyToken
        ) {
          if (!this.routing.isWidget()) {
            localStorage.setItem('token', this.token);
            localStorage.setItem('tokenDataAccess', this.tokenDataAccess);
          }
          this.loadFeatureFlags(this.token).subscribe();
          this.userActivityApi.getSeenArticles().subscribe((articles) => {
            this.userActivityApi.seenArticles = articles;
          });
        }
        if (
          !this.routing.isReader() &&
          !this.routing.isStoryReader() &&
          readonlyToken
        ) {
          this.router.navigate(['/message/autherror']);
        } else {
          this.jwtAuthorized = true;
        }
        if (this.config.appConfig.setup.useStorage) {
          if (sessionStorage.getItem('track_login') !== user.email) {
            sessionStorage.setItem('track_login', user.email);
            if (localStorage.getItem('impersonate') === user.email) {
              this.trackLoginSession(true).subscribe();
            } else {
              this.trackLoginSession(false).subscribe();
            }
          }
        }
      }),
      catchError((error) => this.handleError(error)),
    );
  }

  // JWT payload (email, roles, exp, …). Decode happens once per token via
  // UserInfoService — repeat calls return the cached payload.
  authToken() {
    this.userdata = this.userInfo.user;
    return this.userdata;
  }

  /**
   * logout
   */
  authLogout() {
    this.portfolioApi.invalidateCache();
    this.queryApi.invalidateCache();
    this.userInfo.clear();

    if (this.config.appConfig.setup.useStorage) {
      localStorage.removeItem('token');
      localStorage.removeItem('impersonate');
      sessionStorage.removeItem('track_login');
    }
    this.ngHttpCachingService.clearCache();
    this.featureFlags = Object.assign({}, DEFAULT_FEATURE_FLAGS);
    this.userState.defaultFilter = undefined;
    this.userState.filter = undefined;
    this.router.navigate(['/login']);
  }

  /**
   * change password knowing the old password
   */
  setPassword(password1, password2): Observable<any> {
    if (this.config.appConfig.setup.useStorage) {
      this.token = localStorage.getItem('token');
    }
    return this.customerApi
      .postText('/v5/change_password', {
        old_password: password1,
        new_password: password2,
      })
      .pipe(catchError((error) => this.handleError(error)));
  }

  /**
   * request password reset for a specific email
   */
  requestPasswordReset(email, url): Observable<any> {
    return this.customerApi
      .postText('/v5/request_password_reset', {
        email,
        password_reset_url: url,
      })
      .pipe(catchError((error) => this.handleError(error)));
  }

  /**
   * reset password based on the received token
   */
  resetPassword(email, token, password): Observable<any> {
    return this.customerApi
      .postText('/v5/reset_password', {
        email,
        token,
        new_password: password,
      })
      .pipe(catchError((error) => this.handleError(error)));
  }

  // STATUS ---------------------------------------------------------------
}
